import React, { FC } from 'react';
import { graphql } from 'gatsby';
import Container from 'react-bootstrap/Container';
import classnames from 'classnames';

import ControlledCarousel from 'components/ControlledCarousel';
import DangerouslySetInnerHtml from 'components/common/DangerouslySetInnerHtml';
import GatsbyImage from 'components/common/GatsbyImage';
import useMatchMedia from 'hooks/useMatchMedia';

import { responsiveObj, FEATURES_LIST_BREAKPOINT } from './constants';
import { ITextWithFeaturesProps } from './model';

import './TextWithFeatures.scss';

const TextWithFeatures: FC<ITextWithFeaturesProps> = ({
  title,
  description,
  features,
  image,
  imageAlt,
  sectionBg,
  sectionTheme,
}) => {
  const displayFeaturesAsList = useMatchMedia(`(min-width: ${FEATURES_LIST_BREAKPOINT}px)`);

  return (
    <div
      className={classnames('text-with-features', {
        [`text-with-features--${sectionTheme}`]: sectionTheme,
        [`text-with-features--background-${sectionBg?.label}`]: sectionBg?.label,
      })}
    >
      <Container fluid>
        <DangerouslySetInnerHtml className="text-with-features__title" html={title} />
        <DangerouslySetInnerHtml className="text-with-features__description" html={description} />
        {image ? (
          <GatsbyImage className="text-with-features__image" fluid={image} alt={imageAlt} />
        ) : null}
        {features?.length ? (
          displayFeaturesAsList ? (
            <ul className="text-with-features__list">
              {features.map(({ properties }) => (
                <li key={properties.image.fallbackUrl}>
                  <GatsbyImage
                    fluid={properties.image}
                    alt={properties.imageAlt}
                    objectFit="contain"
                  />
                </li>
              ))}
            </ul>
          ) : (
            <ControlledCarousel
              showDots
              dotsOutside
              classes="text-with-features__carousel"
              responsiveObj={responsiveObj}
            >
              {features.map(({ properties }) => (
                <GatsbyImage
                  key={properties.image.fallbackUrl}
                  className="text-with-features__carousel-item-image"
                  fluid={properties.image}
                  alt={properties.imageAlt}
                />
              ))}
            </ControlledCarousel>
          )
        ) : null}
      </Container>
    </div>
  );
};

export const query = graphql`
  fragment FragmentTextWithFeatures on TTextWithFeaturesStructure {
    structure
    properties {
      anchorName
      showInMenu
      title
      description
      features {
        properties {
          image {
            ...FragmentGatsbyProps
            gatsbyImage {
              childImageSharp {
                fluid(maxWidth: 600, quality: 80) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
          imageAlt
        }
      }
      image {
        ...FragmentGatsbyImageLg
      }
      imageAlt
      sectionBg {
        label
      }
      sectionTheme
    }
  }
`;

export default TextWithFeatures;
